import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { http } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend } from "recharts";
import { CHART_COLORS } from "@/lib/constants";
import { TrendingUp, Users, PhoneCall, Stethoscope, CheckCircle2, Percent, IndianRupee } from "lucide-react";

function withinRange(dateStr, range) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  const start = new Date();
  if (range === "this_month") { start.setDate(1); start.setHours(0,0,0,0); return d >= start; }
  if (range === "last_month") {
    const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const e = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
    return d >= s && d <= e;
  }
  if (range === "last_3_months") {
    const s = new Date(now.getFullYear(), now.getMonth() - 2, 1);
    return d >= s;
  }
  if (range === "all") return true;
  return true;
}

export default function Dashboard() {
  const [range, setRange] = useState("this_month");
  const { data: patients = [], isLoading } = useQuery({
    queryKey: ["patients"],
    queryFn: async () => (await http.get("/patients")).data,
  });

  const filtered = useMemo(() => patients.filter((p) => withinRange(p.enquiry_date, range)), [patients, range]);

  const stats = useMemo(() => {
    const enquiries = filtered.length;
    const consultations = filtered.filter((p) => ["Consultation Booked","Consultation Completed","Converted to Treatment"].includes(p.enquiry_status)).length;
    const consultCompleted = filtered.filter((p) => ["Consultation Completed","Converted to Treatment"].includes(p.enquiry_status)).length;
    const converted = filtered.filter((p) => p.enquiry_status === "Converted to Treatment").length;
    const revenue = filtered.reduce((a, p) => a + (Number(p.revenue) || 0), 0);
    const rate = enquiries ? Math.round((converted / enquiries) * 100) : 0;
    return { enquiries, consultations, consultCompleted, converted, rate, revenue };
  }, [filtered]);

  const bySource = useMemo(() => {
    const m = new Map();
    for (const p of filtered) {
      m.set(p.acquisition_source, (m.get(p.acquisition_source) || 0) + 1);
    }
    return Array.from(m, ([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [filtered]);

  const monthly = useMemo(() => {
    const map = new Map();
    for (const p of patients) {
      if (!p.enquiry_date) continue;
      const k = p.enquiry_date.slice(0, 7);
      map.set(k, (map.get(k) || 0) + 1);
    }
    const keys = Array.from(map.keys()).sort();
    const last6 = keys.slice(-6);
    return last6.map((k) => ({ month: k, count: map.get(k) }));
  }, [patients]);

  const funnel = useMemo(() => {
    const consultsBooked = filtered.filter((p) => ["Consultation Booked","Consultation Completed","Converted to Treatment"].includes(p.enquiry_status)).length;
    const consultsCompleted = filtered.filter((p) => ["Consultation Completed","Converted to Treatment"].includes(p.enquiry_status)).length;
    const conv = filtered.filter((p) => p.enquiry_status === "Converted to Treatment").length;
    return { enquiries: filtered.length, consultsBooked, consultsCompleted, conv };
  }, [filtered]);

  const kpis = [
    { label: "New Patients", value: stats.converted, icon: CheckCircle2, tint: "bg-emerald-50 text-emerald-700" },
    { label: "Enquiries", value: stats.enquiries, icon: PhoneCall, tint: "bg-blue-50 text-blue-700" },
    { label: "Consultations", value: stats.consultCompleted, icon: Stethoscope, tint: "bg-violet-50 text-violet-700" },
    { label: "Converted", value: stats.converted, icon: Users, tint: "bg-amber-50 text-amber-700" },
    { label: "Conversion Rate", value: `${stats.rate}%`, icon: Percent, tint: "bg-rose-50 text-rose-700" },
  ];

  const avgRevenue = stats.converted ? Math.round(stats.revenue / stats.converted) : 0;

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-zinc-900">Dashboard</h1>
          <p className="text-sm text-zinc-500 mt-1">Your patient acquisition at a glance.</p>
        </div>
        <Select value={range} onValueChange={setRange}>
          <SelectTrigger className="w-56" data-testid="date-range-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="this_month">This Month</SelectItem>
            <SelectItem value="last_month">Last Month</SelectItem>
            <SelectItem value="last_3_months">Last 3 Months</SelectItem>
            <SelectItem value="all">All Time</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="text-zinc-500">Loading...</div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            {kpis.map((k) => (
              <Card key={k.label} className="p-4 border-zinc-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)] animate-fade-in-up" data-testid={`kpi-${k.label.toLowerCase().replace(/\s+/g,'-')}`}>
                <div className={`inline-flex items-center justify-center h-8 w-8 rounded-md ${k.tint}`}>
                  <k.icon className="h-4 w-4" strokeWidth={1.75} />
                </div>
                <div className="mt-3 text-2xl font-heading font-semibold text-zinc-900">{k.value}</div>
                <div className="text-xs uppercase tracking-wider text-zinc-500 mt-0.5">{k.label}</div>
              </Card>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Card className="p-5 border-zinc-200">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500 font-semibold">
                <IndianRupee className="h-3.5 w-3.5" /> Revenue from new patients
              </div>
              <div className="mt-2 font-heading text-2xl font-semibold text-zinc-900">₹ {stats.revenue.toLocaleString()}</div>
            </Card>
            <Card className="p-5 border-zinc-200">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500 font-semibold">
                <TrendingUp className="h-3.5 w-3.5" /> Avg revenue per converted patient
              </div>
              <div className="mt-2 font-heading text-2xl font-semibold text-zinc-900">₹ {avgRevenue.toLocaleString()}</div>
            </Card>
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            <Card className="p-5 border-zinc-200 lg:col-span-2">
              <h3 className="font-heading text-base font-semibold text-zinc-900">Where are my new patients coming from?</h3>
              <p className="text-xs text-zinc-500 mt-0.5">By acquisition source in the selected period.</p>
              <div className="h-64 mt-3">
                {bySource.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={bySource} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                      <CartesianGrid stroke="#F1F5F9" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#71717A" }} interval={0} angle={-20} textAnchor="end" height={60} />
                      <YAxis tick={{ fontSize: 11, fill: "#71717A" }} allowDecimals={false} />
                      <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E4E4E7", fontSize: 12 }} />
                      <Bar dataKey="value" fill="#059669" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <EmptyState label="No records in this period yet." />}
              </div>
            </Card>

            <Card className="p-5 border-zinc-200">
              <h3 className="font-heading text-base font-semibold text-zinc-900">Source share</h3>
              <div className="h-64 mt-3">
                {bySource.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={bySource} innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value">
                        {bySource.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E4E4E7", fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <EmptyState label="Add patients to see the mix." />}
              </div>
            </Card>
          </div>

          <Card className="p-5 border-zinc-200" data-testid="funnel-card">
            <h3 className="font-heading text-base font-semibold text-zinc-900">Your patient acquisition funnel</h3>
            <div className="mt-4 grid sm:grid-cols-4 gap-3">
              <FunnelStep n={funnel.enquiries} label="Enquiries" />
              <FunnelStep n={funnel.consultsBooked} label="Consultations Booked" />
              <FunnelStep n={funnel.consultsCompleted} label="Consultations Completed" />
              <FunnelStep n={funnel.conv} label="Converted to Treatment" highlight />
            </div>
            <div className="mt-4 grid sm:grid-cols-3 gap-3 text-sm">
              <Metric label="Enquiry → Consultation" value={pct(funnel.consultsBooked, funnel.enquiries)} />
              <Metric label="Consultation → Treatment" value={pct(funnel.conv, funnel.consultsCompleted)} />
              <Metric label="Overall Conversion" value={pct(funnel.conv, funnel.enquiries)} />
            </div>
          </Card>

          <Card className="p-5 border-zinc-200">
            <h3 className="font-heading text-base font-semibold text-zinc-900">New patients — last 6 months</h3>
            <div className="h-64 mt-3">
              {monthly.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={monthly}>
                    <CartesianGrid stroke="#F1F5F9" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#71717A" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#71717A" }} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E4E4E7", fontSize: 12 }} />
                    <Line type="monotone" dataKey="count" stroke="#059669" strokeWidth={2} dot={{ r: 4, fill: "#059669" }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : <EmptyState label="No enquiries yet." />}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

function pct(a, b) { return b ? Math.round((a / b) * 100) + "%" : "—"; }

function FunnelStep({ n, label, highlight }) {
  return (
    <div className={`rounded-md p-4 border ${highlight ? "border-emerald-300 bg-emerald-50" : "border-zinc-200 bg-white"}`}>
      <div className="font-heading text-2xl font-semibold text-zinc-900">{n}</div>
      <div className="text-xs uppercase tracking-wider text-zinc-500 mt-0.5">{label}</div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="border border-zinc-200 rounded-md p-3">
      <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">{label}</div>
      <div className="mt-1 font-heading text-lg font-semibold text-zinc-900">{value}</div>
    </div>
  );
}

function EmptyState({ label }) {
  return <div className="h-full flex items-center justify-center text-sm text-zinc-400">{label}</div>;
}
